import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import {BaseService} from './base.service';
import {SimulateMultiResult} from '../pages/eso/simulate-multi/models/simulate-multi-result.model';
import {SimulateMultiTrainingResult} from '../pages/eso/simulate-multi/models/simulate-multi-training-result.model';
import {SimulationProgressDto} from '../pages/eso/simulate-multi/models/simulation-progress-dto';

@Injectable({ providedIn: 'root' })
export class SignalRService {
	private connection?: signalR.HubConnection;
	private started = false;

	private static readonly PROGRESS_HUB_CONNECTION_ENDPOINT = 'progressHub';

	public onReceiveSimulationInitiated: Subject<SimulateMultiResult> = new Subject<SimulateMultiResult>();
	public onReceiveSimulationProgressUpdate: Subject<SimulateMultiResult> = new Subject<SimulateMultiResult>();

	public onReceiveSimulationTrainingInitiated: Subject<SimulateMultiTrainingResult> = new Subject<SimulateMultiTrainingResult>();
	public onReceiveSimulationTrainingProgressUpdate: Subject<SimulateMultiTrainingResult> = new Subject<SimulateMultiTrainingResult>();

	constructor(private ngZone: NgZone, private baseService: BaseService) {}

	public async startConnection(): Promise<void> {
		if (this.started && this.connection?.state === signalR.HubConnectionState.Connected) {
			return;
		}

		if (!this.connection) {
			this.connection = new signalR.HubConnectionBuilder()
				.withUrl(`${environment.apiEndpoint}/${SignalRService.PROGRESS_HUB_CONNECTION_ENDPOINT}`)
					/*{
						accessTokenFactory: () => this.baseService.getNewOptions().headers['authorization'],
					})*/
				.withAutomaticReconnect()
				.configureLogging(signalR.LogLevel.Information)
				.build();

			this.registerHandlers();
		}

		if (this.connection.state !== signalR.HubConnectionState.Connected) {
			await this.connection.start();
			this.started = true;
		}
	}

	public async stopConnection(): Promise<void> {
		if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) {
			await this.connection.stop();
		}
		this.started = false;
	}

	public async joinSimulationGroup(simulateMultiResultId: number): Promise<void> {
		await this.ensureConnected();
		await this.connection!.invoke('JoinSimulationGroup', simulateMultiResultId);
	}

	public async leaveSimulationGroup(simulateMultiResultId: number): Promise<void> {
		if (this.connection?.state === signalR.HubConnectionState.Connected) {
			await this.connection.invoke('LeaveSimulationGroup', simulateMultiResultId);
		}
	}

	private async ensureConnected(): Promise<void> {
		if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
			await this.startConnection();
		}
	}

	private registerHandlers(): void {
		if (!this.connection) return;

		this.connection.on('ReceiveSimulationInitiated', (payload: SimulateMultiResult) => {
			this.runInZone(() => this.onReceiveSimulationInitiated.next(payload));
		});

		this.connection.on('ReceiveSimulationProgressUpdate', (payload: any) => {
			const normalized = this.normalizeProgressPayload(payload);
			if (normalized) {
				this.runInZone(() => this.onReceiveSimulationProgressUpdate.next(normalized));
			}
		});

		this.connection.on('ReceiveSimulationTrainingInitiated', (payload: SimulateMultiTrainingResult) => {
			this.runInZone(() => this.onReceiveSimulationTrainingInitiated.next(payload));
		});

		this.connection.on('ReceiveSimulationTrainingProgressUpdate', (payload: SimulateMultiTrainingResult) => {
			this.runInZone(() => this.onReceiveSimulationTrainingProgressUpdate.next(payload));
		});
	}

	private normalizeProgressPayload(payload: any): SimulateMultiResult | null {
		if (payload && typeof payload === 'object' && 'SimulationsCompletedQuantity' in payload) {
			return payload as SimulateMultiResult;
		}

		const dto = payload as SimulationProgressDto | undefined;
		if (!dto || typeof dto.id !== 'number' || typeof dto.completed !== 'number' || typeof dto.total !== 'number') {
			return null;
		}

		const isComplete = dto.completed >= dto.total && dto.total > 0;

		const now = isComplete ? new Date() : undefined;

		const minimal: SimulateMultiResult = {
			Id: dto.id,
			Name: undefined as any,
			StartTime: now ?? undefined as any,
			EndTime: now ?? undefined as any,
			CompletionTime: undefined as any,
			UserId: undefined as any,
			UserName: undefined as any,
			SelectedQuantity: undefined as any,
			SimulationsQuantity: dto.total as any,
			SimulationsCompletedQuantity: dto.completed as any,
			Status: (isComplete ? 'Success' : 'In Progress') as any
		} as unknown as SimulateMultiResult;

		return minimal;
	}

	private runInZone(fn: () => void): void {
		if (this.ngZone) {
			this.ngZone.run(fn);
		} else {
			fn();
		}
	}
}
